import dayjs from 'dayjs'
import Decimal from 'decimal.js'
import type { ReduxInvoices } from 'src/types/apiType'

const CREDIT_NOTE_MATCH_EPSILON = new Decimal('0.0001')

export type DueBasedInvoiceStatus = 'Pending' | 'Overdue'

export type DerivedOriginalInvoiceStatus =
	| 'Archived'
	| 'Nullified'
	| 'Overdue'
	| 'Paid'
	| 'Partially Paid'
	| 'Pending'

export function getDueBasedInvoiceStatus(
	invoiceDueDate?: string | null
): DueBasedInvoiceStatus {
	if (!invoiceDueDate || !dayjs(invoiceDueDate).isValid()) {
		return 'Pending'
	}

	return dayjs(invoiceDueDate).diff(dayjs(), 'day') >= 0
		? 'Pending'
		: 'Overdue'
}

export function sumPaidCreditNotes(
	linkedCreditNotes: Array<ReduxInvoices | null | undefined>
): Decimal {
	return linkedCreditNotes.reduce((total, creditNote) => {
		if (
			!creditNote ||
			creditNote.paymentStatus !== 'Paid' ||
			creditNote.invoiceType !== 'credit'
		) {
			return total
		}

		return total.add(new Decimal(creditNote.amountAfterTax ?? 0))
	}, new Decimal(0))
}

export function deriveOriginalInvoiceStatus({
	originalInvoice,
	linkedCreditNotes,
}: {
	originalInvoice: ReduxInvoices
	linkedCreditNotes: ReduxInvoices[]
}): DerivedOriginalInvoiceStatus {
	if (originalInvoice.paymentStatus === 'Archived') {
		return 'Archived'
	}

	if (originalInvoice.paymentStatus === 'Paid') {
		return 'Paid'
	}

	const paidCreditTotal = sumPaidCreditNotes(linkedCreditNotes)
	const originalInvoiceTotal = new Decimal(
		originalInvoice.amountAfterTax ?? 0
	)
	const statusByDueDate = getDueBasedInvoiceStatus(
		originalInvoice.invoiceDueDate
	)

	if (
		paidCreditTotal.greaterThan(0) &&
		paidCreditTotal.greaterThanOrEqualTo(
			originalInvoiceTotal.minus(CREDIT_NOTE_MATCH_EPSILON)
		)
	) {
		return 'Nullified'
	}

	if (paidCreditTotal.greaterThan(0)) {
		return statusByDueDate === 'Pending' ? 'Partially Paid' : 'Overdue'
	}

	return statusByDueDate
}

export async function recomputeOriginalInvoiceStatus({
	tenantId,
	originalInvoiceId,
}: {
	tenantId?: string | null
	originalInvoiceId?: string | null
}): Promise<void> {
	if (!tenantId || !originalInvoiceId) {
		return
	}

	const [
		{ API, graphqlOperation },
		{ updateInvoicesSimplified },
		{ fetchInvoicesByTenantIdUtil },
	] = await Promise.all([
		import('src/utils/amplify/legacy'),
		import('src/graphql/customMutations'),
		import('src/utils/fetchUtils'),
	])
	const allInvoices = await fetchInvoicesByTenantIdUtil(tenantId)
	const originalInvoice = allInvoices.find(
		(invoice) =>
			invoice.id === originalInvoiceId && invoice.invoiceType !== 'credit'
	)

	if (!originalInvoice) {
		return
	}

	const linkedCreditNotes = allInvoices.filter(
		(invoice) =>
			invoice.invoiceType === 'credit' &&
			invoice.originalInvoiceId === originalInvoiceId &&
			invoice.activeFlag
	)
	const nextStatus = deriveOriginalInvoiceStatus({
		originalInvoice,
		linkedCreditNotes,
	})

	if (nextStatus === originalInvoice.paymentStatus) {
		return
	}

	await API.graphql(
		graphqlOperation(updateInvoicesSimplified, {
			input: {
				id: originalInvoice.id,
				paymentStatus: nextStatus,
			},
		})
	)
}
